import { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";

import Valkyrie, { viAngleDown, viAngleUp } from "@sippy-platform/valkyrie";

import {
  Box,
  Collapse,
  Fade,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Popper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";

function ParentItem({ title, ...props }) {
  const location = useLocation();
  const theme = useTheme();
  const matchesLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const drawerExpanded = useSelector((state) => state.ui.drawerExpanded);
  const drawerOpen = useSelector((state) => state.ui.drawerOpen);

  const [open, setOpen] = useState(false);
  const [openCollapsible, setOpenCollapsible] = useState(() => location?.pathname?.includes(props.link));
  const [anchorEl, setAnchorEl] = useState(null);

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? "transition-popper" : undefined;

  if (drawerExpanded || (drawerOpen && !matchesLgUp)) {
    return (
      <>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setOpenCollapsible((prev) => !prev)}
            selected={location?.pathname?.includes(props.link) && !openCollapsible}
          >
            <ListItemIcon>
              <Valkyrie icon={props.icon} />
            </ListItemIcon>
            <ListItemText primary={title} />
            <ListItemIcon sx={{ minWidth: 16 }}>
              <Valkyrie icon={openCollapsible ? viAngleUp : viAngleDown} />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
        <Collapse
          in={openCollapsible}
          timeout="auto"
          unmountOnExit
          sx={{
            minHeight: "auto !important",
            display: "block",
          }}
        >
          <List
            sx={{
              ".MuiListItem-root": {
                minHeight: 36,
              },
              ".MuiListItemButton-root": {
                minHeight: 36,
                py: 0,
                pr: 1.5,
                pl: 4,
              },
              ".MuiListItemText-primary": {
                fontWeight: "600",
              },
            }}
            component="nav"
          >
            {props.children}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <Box onMouseLeave={() => setOpen(false)} onMouseEnter={() => setOpen(true)}>
      <ListItem disablePadding>
        <ListItemButton ref={setAnchorEl} selected={location?.pathname?.includes(props.link) || open}>
          <ListItemIcon>
            <Valkyrie icon={props.icon} />
          </ListItemIcon>
          <ListItemText primary={title} />
        </ListItemButton>
      </ListItem>
      <Popper id={id} open={open} anchorEl={anchorEl} placement="right-start" transition sx={{ zIndex: 1200 }}>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              elevation={8}
              sx={{
                width: 220,
                boxShadow: "md",
                borderRadius: 1.5,
                ml: 2,
                mb: 1,
                bgcolor: "background.menu",
                maxHeight: "calc(100dvh - 16px)",
                overflow: "auto",
              }}
            >
              <List
                sx={{
                  p: 1,
                  pt: 0,
                  ".MuiListItem-root": {
                    minHeight: 40,
                  },
                  ".MuiListItemButton-root": {
                    minHeight: 40,
                    py: 0,
                    pr: 1.5,
                    pl: 1.25,
                  },
                  ".MuiListItemText-primary": {
                    fontSize: ".875rem",
                    fontWeight: "600",
                  },
                }}
                component="nav"
              >
                <ListSubheader sx={{ mt: 0, pt: 0, px: 0.5, minHeight: 0, lineHeight: 3, bgcolor: "transparent" }}>
                  {title}
                </ListSubheader>
                {props.children}
              </List>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
}

ParentItem.propTypes = {
  title: PropTypes.string,
  icon: PropTypes.object,
  children: PropTypes.node,
  id: PropTypes.string,
  link: PropTypes.string,
};

export default ParentItem;
